import { Link } from 'react-router-dom';

const LAST_UPDATED = 'May 15, 2026';

export default function Privacy() {
  return (
    <div className="legal">
      <h1>Privacy Policy</h1>
      <p className="muted">Last updated: {LAST_UPDATED}</p>

      <p>
        Invitey is a multi-tenant calendar booking service operated by{' '}
        <strong>Get Proof Digital Inc.</strong>, a Saskatchewan corporation
        doing business as &ldquo;Proof Digital&rdquo; and, for the service
        described here, as &ldquo;Invitey&rdquo; (collectively
        &ldquo;Invitey&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;). This policy
        explains what information we collect, how we use it, who we share it
        with, and the choices you have. It applies to <code>invitey.ca</code>,
        embedded booking widgets we serve, and custom hostnames (e.g.{' '}
        <code>book.customer.com</code>) that route to Invitey.
      </p>

      <h2>Who is the data controller?</h2>
      <p>
        Invitey is operated by <strong>Get Proof Digital Inc.</strong>
        (&ldquo;Proof Digital&rdquo;). When a business (the
        &ldquo;Organization&rdquo;) uses Invitey to take bookings from its own
        customers, the Organization is the controller of those customer
        records, and Invitey acts as the processor on the Organization&rsquo;s
        behalf. For account holders (Organization admins and staff), Invitey
        is the controller of account data.
      </p>

      <h2>Information we collect</h2>
      <h3>From Organization admins and staff</h3>
      <ul>
        <li>
          <strong>Identity and account data</strong> &mdash; name, email, and
          authentication credentials, managed by our authentication provider
          (Clerk). We do not store passwords.
        </li>
        <li>
          <strong>Organization data</strong> &mdash; organization name, slug,
          locations, services, staff roster, availability rules, time-off
          windows, and (for embedded use) custom hostnames.
        </li>
        <li>
          <strong>Calendar OAuth tokens</strong> &mdash; access and refresh
          tokens for the Google Calendar or Microsoft Outlook account a staff
          member chooses to connect. Tokens are stored encrypted at rest in
          Cloudflare D1 and are scoped to that single staff member.
        </li>
        <li>
          <strong>API keys</strong> &mdash; we store only a SHA-256 hash of
          each API key. The raw key is shown once at creation and never again.
        </li>
      </ul>

      <h3>From the people booking appointments (&ldquo;bookees&rdquo;)</h3>
      <ul>
        <li>Name, email, and (optionally) phone number.</li>
        <li>The location, service, staff member, and time slot chosen.</li>
        <li>Any free-text notes provided at booking time.</li>
      </ul>

      <h3>From inbound SMS replies</h3>
      <p>
        The phone number Invitey uses to send appointment reminders is a
        send-only number, but if a bookee replies to it we receive that
        reply through our SMS provider. We log the sender&rsquo;s phone
        number, the message body, and the timestamp so the message is not
        silently lost &mdash; for example, so we can tell the Organization a
        customer is trying to reach them, and so we can honour STOP and
        UNSUBSCRIBE requests. We do not use inbound SMS content for
        advertising or analytics.
      </p>

      <h3>Automatic / technical data</h3>
      <ul>
        <li>
          Request metadata (IP address, user agent, referrer, timestamp) is
          processed by Cloudflare for routing, security, and abuse prevention.
        </li>
        <li>
          We do not use third-party analytics or advertising cookies. The only
          cookies set by Invitey itself are functional cookies required for
          authentication.
        </li>
      </ul>

      <h2 id="google">Google user data &mdash; Limited Use disclosure</h2>
      <p>
        Invitey&rsquo;s use and transfer of information received from Google
        APIs adheres to the{' '}
        <a
          href="https://developers.google.com/terms/api-services-user-data-policy"
          target="_blank"
          rel="noopener noreferrer"
        >
          Google API Services User Data Policy
        </a>
        , including the <strong>Limited Use requirements</strong>.
      </p>
      <p>
        When a staff member connects their Google account to Invitey, we
        request the following OAuth scopes:
      </p>
      <ul>
        <li>
          <code>openid</code>, <code>email</code>, <code>profile</code>{' '}
          &mdash; to identify which Google account was connected and display
          the staff member&rsquo;s email address back to them in the dashboard.
        </li>
        <li>
          <code>https://www.googleapis.com/auth/calendar.readonly</code>{' '}
          &mdash; to read free/busy information from the staff member&rsquo;s
          calendars so we do not double-book existing events.
        </li>
        <li>
          <code>https://www.googleapis.com/auth/calendar.events</code> &mdash;
          to create, update, and cancel the calendar event that corresponds to
          a booking made through Invitey.
        </li>
      </ul>
      <p>
        We use Google user data <strong>only</strong> to provide the
        user-facing scheduling features described above:
      </p>
      <ul>
        <li>
          We do not use Google user data to train generalized AI or machine
          learning models.
        </li>
        <li>
          We do not transfer Google user data to third parties except as
          necessary to provide the service (the calendar event is sent to
          Google itself on the staff member&rsquo;s behalf), to comply with
          applicable law, or as part of a merger, acquisition, or sale of
          assets &mdash; in which case the new entity is bound by this policy.
        </li>
        <li>
          We do not allow humans to read Google user data except (a) with the
          staff member&rsquo;s explicit consent, (b) for security purposes such
          as investigating abuse, (c) to comply with applicable law, or (d) in
          aggregated, anonymized form for internal operations.
        </li>
        <li>
          We do not use Google user data to serve advertising of any kind,
          including retargeting or personalized advertising.
        </li>
      </ul>
      <p>
        A staff member can revoke Invitey&rsquo;s access to their Google
        account at any time by visiting{' '}
        <a
          href="https://myaccount.google.com/permissions"
          target="_blank"
          rel="noopener noreferrer"
        >
          Google Account &rarr; Security &rarr; Third-party access
        </a>
        , or by clicking &ldquo;Disconnect&rdquo; in their Invitey staff
        dashboard. Disconnecting revokes the token with Google and deletes the
        token row from our database.
      </p>

      <h2>Microsoft Outlook user data</h2>
      <p>
        The same principles apply to Microsoft Outlook calendar connections.
        We request the <code>Calendars.ReadWrite</code> Microsoft Graph scope,
        plus <code>openid</code>, <code>email</code>, <code>profile</code>, and{' '}
        <code>offline_access</code>, and use that data only to read free/busy
        and write the booking event on the staff member&rsquo;s behalf.
      </p>

      <h2>How we use information</h2>
      <ul>
        <li>To provide and operate the booking service.</li>
        <li>
          To send transactional notifications about bookings (confirmation,
          reminder, reschedule, cancellation) by email and, where the bookee
          opted in, by SMS.
        </li>
        <li>
          To send outbound webhooks to the Organization&rsquo;s configured
          endpoint when a booking event occurs.
        </li>
        <li>
          To protect the service against abuse, fraud, and security incidents.
        </li>
        <li>To comply with applicable law and respond to legal process.</li>
      </ul>

      <h2>Subprocessors and third parties</h2>
      <p>We rely on the following subprocessors to deliver the service:</p>
      <ul>
        <li>
          <strong>Cloudflare</strong> &mdash; hosting (Workers), database (D1),
          DNS, and edge security.
        </li>
        <li>
          <strong>Clerk</strong> &mdash; authentication and organization
          membership for admins and staff.
        </li>
        <li>
          <strong>Google</strong> &mdash; calendar integration for staff who
          connect a Google account.
        </li>
        <li>
          <strong>Microsoft</strong> &mdash; calendar integration for staff
          who connect an Outlook account.
        </li>
        <li>
          <strong>Mailgun</strong> &mdash; transactional email delivery.
        </li>
        <li>
          <strong>Twilio</strong> &mdash; transactional SMS delivery.
        </li>
        <li>
          <strong>Google Maps Platform</strong> &mdash; place autocomplete and
          geocoding for location records (no end-user data is sent to Google
          Maps).
        </li>
      </ul>

      <h2>Data retention</h2>
      <ul>
        <li>
          Bookings, locations, services, and staff records are retained for
          the life of the Organization&rsquo;s account.
        </li>
        <li>
          Calendar OAuth tokens are retained until the staff member disconnects
          or the Organization is deleted, whichever comes first.
        </li>
        <li>
          Webhook delivery logs are retained for 90 days.
        </li>
        <li>
          When an account is deleted, we delete or anonymize associated
          personal data within 30 days, except where we are required to retain
          it to comply with law.
        </li>
      </ul>

      <h2>Security</h2>
      <p>
        We use HTTPS everywhere, store OAuth tokens encrypted at rest, store
        only hashed API keys, and verify webhook signatures using HMAC-SHA256.
        No system is perfectly secure; if we become aware of a breach affecting
        your data, we will notify you and the relevant authorities as required
        by law.
      </p>

      <h2>Your rights</h2>
      <p>
        Depending on where you live (GDPR, CCPA, PIPEDA, etc.), you may have
        the right to access, correct, delete, port, or object to processing of
        your personal data. If you are a bookee, please direct these requests
        to the Organization you booked with; we will assist them as their
        processor. If you are an Organization admin or staff member, contact
        us directly at the address below.
      </p>

      <h2>Children</h2>
      <p>
        Invitey is not directed to children under 13 (or the local equivalent
        age of digital consent), and we do not knowingly collect personal data
        from them.
      </p>

      <h2>International transfers</h2>
      <p>
        Invitey is operated from Canada. Our subprocessors operate globally,
        so your data may be processed in the United States, the European Union,
        or other jurisdictions. We rely on the subprocessors&rsquo; own legal
        safeguards (standard contractual clauses or equivalent) for transfers.
      </p>

      <h2>Changes to this policy</h2>
      <p>
        We will update the &ldquo;Last updated&rdquo; date at the top of this
        page when this policy changes. Material changes will be communicated
        to Organization admins by email.
      </p>

      <h2>Contact</h2>
      <p>
        Privacy questions or requests can be sent to{' '}
        <a href="mailto:privacy@invitey.ca">privacy@invitey.ca</a> or by post
        to Get Proof Digital Inc., c/o Invitey, Saskatchewan, Canada.
      </p>

      <p className="muted">
        <Link to="/">&larr; Back to home</Link> &middot;{' '}
        <Link to="/terms">Terms of Service</Link>
      </p>
    </div>
  );
}
